import { useCloseContextMenuOnScroll } from 'hooks/useCloseContextMenuOnScroll';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

type TableRowActionsButtonProps = {
    id: number;
    onEdit: (id: number) => () => void;
    onRemove: (id: number) => () => void;
    canEdit: boolean;
    canDelete: boolean;
    disabled?: boolean;
};

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');

export const TableRowActionsButton = ({
    id,
    onEdit,
    onRemove,
    canEdit,
    canDelete,
    disabled,
}: TableRowActionsButtonProps) => {
    const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);

    useCloseContextMenuOnScroll({ setOpen: setIsContextMenuOpen });

    const rowActionsButtonRef = useRef(null);

    const handleRowActionsButtonClick = useCallback(() => {
        setIsContextMenuOpen((prevState) => !prevState);
    }, []);

    const handleContextMenuItemClick = useCallback(() => {
        handleRowActionsButtonClick();
    }, [handleRowActionsButtonClick]);

    const contextItems = useMemo(() => {
        const items = [];

        if (canEdit) {
            items.push({
                label: 'Редактировать',
                action: () => {
                    onEdit(id)();
                    handleContextMenuItemClick();
                },
            });
        }

        if (canDelete) {
            items.push({
                label: 'Удалить',
                action: () => {
                    onRemove(id)();
                    handleContextMenuItemClick();
                },
            });
        }

        return items;
    }, [canDelete, canEdit, handleContextMenuItemClick, id, onEdit, onRemove]);

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                onClick={handleRowActionsButtonClick}
                ref={rowActionsButtonRef}
                disabled={disabled ?? (!canEdit && !canDelete)}
            />
            <ContextMenu
                isOpen={isContextMenuOpen}
                items={contextItems}
                getItemLabel={getItemLabel}
                anchorRef={rowActionsButtonRef}
                direction="downStartLeft"
                getItemOnClick={getItemOnClick}
                onClickOutside={handleRowActionsButtonClick}
            />
        </>
    );
};

TableRowActionsButton.displayName = 'TableRowActionsButton';
